import { cssStyle,anime } from "./scripts/css-style";
import { focusInBlock, getFocusableElements } from "./scripts/focus-in-block";
import { StyleOptions } from "./scripts/interfaces";
import { ProgressFormType, PreventType, ProgressingType, RenderedStyle } from "./scripts/types";

/**
 * ProgressForm represents a class for managing a progressive form.
 */
export default class ProgressForm
{
    protected translateX:number;
    protected fieldsetLength:number = 0;
    protected fieldsetMarginWidth: number;
    protected progressOptions: ProgressFormType;
    protected progressingData:ProgressingType = {};
    protected lastIndex: number;
    protected isLazyProgress: boolean;
    protected lazyFieldsetLength: number;
    protected renderedStyle:RenderedStyle;
    protected animation: boolean;
    protected fieldSetElement:HTMLElement|null = null;
    /**
     * @param enableDefaultCssStyle Determines whether the default CSS style should be enabled. Default is true.
     */
    constructor(public form:HTMLFormElement, public enableDefaultCssStyle:boolean = true){}
    /**
     * Retrieves and initializes all data and variables for running
     * @param progressOptions The parameters of the form.
     * @returns 
     */
    protected init(progressOptions:ProgressFormType)
    {
        this.progressOptions = progressOptions;
        this.animation = progressOptions?.animation
        this.hasValidHTMLStructure(this.form);
        const fieldSets = this.form.querySelectorAll('fieldset');
        this.setTranslateX(fieldSets[0],progressOptions);
        const progressElement = document.querySelector('[__progress__]') as HTMLElement;
        this.fieldsetMarginWidth = progressOptions?.fieldsetMargingWidth ?? 60;
        let nextIndex = 1;
        let prevIndex = this.isLazyProgress ? this.lazyFieldsetLength : fieldSets.length;
        let prevTranslateX = 0;
        this.fieldsetLength = this.lastIndex = this.isLazyProgress ? this.lazyFieldsetLength : fieldSets.length;
        const {PROGRESS} = this;
        return {
            fieldSets,nextIndex,
            prevTranslateX,PROGRESS,
            prevIndex,progressElement
        }
    }
    /**
     * Executes the progressive form.
     * @param params The parameters of the form.
     * @param styleOptions Style options for the form, fieldsets and fieldsets parent elements.
    */
    public run(progressOptions?:ProgressFormType,styleOptions?: StyleOptions, preventProgress:PreventType = false)
    {
        let {fieldSets,nextIndex,prevTranslateX,PROGRESS,prevIndex,progressElement} = this.init(progressOptions);
        if(fieldSets && !this.isLazyProgress && fieldSets.length > 1){
            let translateX = this.translateX - this.fieldsetMarginWidth / this.fieldsetLength;
            const targetMarginWidth = progressOptions?.targetMarginWidth ?? 0;
            fieldSets.forEach((fieldSet,i) => {
                const nextButton = fieldSet.querySelector("[__next__]") as HTMLElement;
                const prevButton = fieldSet.querySelector("[__prev__]") as HTMLElement;
                let nextTranslateX = (translateX * nextIndex) - targetMarginWidth;
                prevTranslateX  = (translateX * nextIndex) + Math.abs(translateX * 2);
                const nextProgress = PROGRESS * (i + 2);
                const prevProgress = i > 1 ? PROGRESS * i : PROGRESS;
                this.fieldSetElement = fieldSet;
                fieldSet.classList.add(`fieldset${i}`);
                if(i === 0){
                    const fields = fieldSet.querySelectorAll("input:not([type='hidden'],[readonly]), textarea") as NodeListOf<HTMLInputElement>;
                    if(fields && fields.length > 0)fields[i].focus();
                    this.compartmentalizeFocusInFieldset(this.fieldSetElement);
                }
                this.progressingData[`fieldset${i}`] = {
                    '@i':i,
                    '@translateX': i === 0 ? 0 : ( i === 1 ? translateX : nextTranslateX - translateX),
                    '@progress': PROGRESS * (i + 1),
                    '@target':this.fieldSetElement,
                    next:{i:nextIndex,button:nextButton,translateX:nextTranslateX,progress:nextProgress},
                    prev:{i:i-1,button:prevButton,translateX:prevTranslateX,progress:prevProgress},
                }
                if(preventProgress === false || preventProgress === "prev")this.next(nextIndex, nextTranslateX, nextButton, progressElement, nextProgress);
                nextIndex++;
                this.formatProgressingData(i);
                if(i === 0){getFocusableElements(this.fieldSetElement);}
                if(preventProgress === false || preventProgress === "next")this.prev(prevIndex, prevTranslateX, prevButton, progressElement, prevProgress);
                prevIndex--;
                
            }); 
        }
        if(progressElement){progressElement.style.width = `${PROGRESS}%`;}
        if(this.enableDefaultCssStyle)
        {this.renderedStyle = cssStyle(this.form,fieldSets,this.translateX,this.fieldsetLength, this.fieldsetMarginWidth,styleOptions)}
    }
    /**
     * Handles the "next" button click event.
     * @param nextIndex The index of the next fieldset.
     * @param nextTranslateX The translateX value for the next fieldset.
     * @param nextButton The "next" button element.
     * @param progressElement The progress element.
     * @param nextProgress The progress for the next fieldset.
     */
    public next(nextIndex: number, nextTranslateX:number,nextButton?: HTMLElement,  progressElement?:HTMLElement,nextProgress?:number)
    {
        const targets = this.fieldsetTargetArray;
        const handleNext = () => {
            const fieldSet = this.form.querySelector(`.fieldset${nextIndex - 1}`) as HTMLElement;
            const isValidFieldset = this.isValidFieldset(fieldSet);
            if(this.progressOptions && this.progressOptions.onPreNext) this.progressOptions.onPreNext(isValidFieldset);
            if(isValidFieldset){
                this.fieldSetElement = document.querySelector(`.fieldset${nextIndex}`) as HTMLElement;
                if(this.fieldSetElement){
                    this.compartmentalizeFocusInFieldset(this.fieldSetElement);
                    getFocusableElements(this.fieldSetElement);
                }
                anime({
                    isAnimate:this.animation,
                    targets: targets,
                    translateX: nextTranslateX,
                    form:this.form
                });
                if(progressElement)
                {progressElement.style.width = `${nextProgress??0}%`;}
                if(this.progressOptions && this.progressOptions.onPostNext) this.progressOptions.onPostNext();
            }
        }
        try {
            if(nextButton){
                nextButton.addEventListener("click", (e)=>{
                    e.preventDefault();
                    handleNext()
                });
            }else{
                if(this.isLazyProgress){
                    handleNext();
                }
                
            }
        } catch (error) {
            console.error(error);
        }
    }
    /**
     * Handles the "previous" button click event.
     * @param prevIndex The index of the previous fieldset.
     * @param prevTranslateX The translateX value for the previous fieldset.
     * @param prevButton The "previous" button element.
     * @param progressElement The progress element.
     * @param prevProgress The progress for the previous fieldset.
    */
    public prev(prevIndex:number, prevTranslateX:number,prevButton?:HTMLElement, progressElement?:HTMLElement, prevProgress?:number)
    {
        const targets = this.fieldsetTargetArray;
        const handlePrev = () => {
            this.fieldSetElement = this.form.querySelector(`.fieldset${prevIndex}`);
            if(this.fieldSetElement){
                this.compartmentalizeFocusInFieldset(this.fieldSetElement);
                getFocusableElements(this.fieldSetElement);
            }
            anime({
                isAnimate:this.animation,
                targets: targets,
                translateX: prevTranslateX,
                form:this.form
            });
            if(progressElement)
            {progressElement.style.width = `${prevProgress??0}%`;}
        }
        try {
            if(prevButton){
                prevButton.addEventListener("click", (e)=>{
                    e.preventDefault();
                    handlePrev();
                 });
            }
        } catch (error) {
            console.error(error);
        }
    }
    /**
     * Calculates the progress percentage of the form.
     * @returns The progress percentage.
    */
    public get PROGRESS()
    {
        return 100 / this.fieldsetLength;
    }
    /**
     * Retrieve progress data for each fieldset
     */
    public get PROGRESSING_DATA()
    {
        return this.progressingData;
    }
    public get RENDERED_STYLE()
    {
        return this.renderedStyle;
    }
    protected formatProgressingData(i:number)
    {
        if(this.progressingData && Object.entries(this.progressingData).length > 0){
            if(i === 0)delete this.progressingData[`fieldset${i}`].prev;
            if(i === this.lastIndex - 1)delete this.progressingData[`fieldset${i}`].next;
        }
    }
    /**
     * Compartmentalizes focus within a fieldset.
     * @param fieldSet The fieldset to compartmentalize focus within.
    */
    protected compartmentalizeFocusInFieldset(fieldSet:HTMLElement)
    {
        window.addEventListener('keydown', (e) => {
            if (e.key === 'Tab' && fieldSet !== null){
                focusInBlock(e, fieldSet);
            }
        })
    }
    /**
     * Checks if a fieldset is valid.
     * @param fieldSet The fieldset to validate.
     * @returns true if the fieldset is valid, otherwise false.
    */
    protected isValidFieldset(fieldSet: HTMLElement): boolean
    {
        if(fieldSet){
            const fields =  Array.from(fieldSet.querySelectorAll('input, select, textarea'));
            if(fields){
                for(const field of fields as HTMLInputElement[]){
                    if(!field.checkValidity()){
                        field.reportValidity();
                        return false;
                    }
                }
            }
        }
        return true;
    }
    /**
     * Generates an array of target selectors for fieldsets.
     * @returns An array of target selectors.
     */
    protected get fieldsetTargetArray(): string[]
    {
        let fieldsetTargetArray = []
        const length = this.lazyFieldsetLength ? this.lazyFieldsetLength : this.fieldsetLength;
        if(length > 0){
            for(let i = 0; i < length; i++) {
                fieldsetTargetArray.push(`.fieldset${i}`);
            }
        }
        return fieldsetTargetArray;
    }
    /**
     * Checks if the HTML structure of the form is valid.
     * @param form The form to validate.
     * @throws Error if the HTML structure is not valid.
     */
    protected hasValidHTMLStructure(form:HTMLFormElement){
        try {
            if(!form){
                throw new Error("The form could not be retrieved");
            }
            const fieldSetParent = form.querySelector("[fieldset__parent]") as HTMLElement;
            if(!fieldSetParent){
                throw new Error("The element with the attribute [fieldset__parent] not found in your form");
            }
            const fieldsetContainer = fieldSetParent.querySelector("[fieldset__container]") as HTMLElement;
            if(!fieldsetContainer){
                throw new Error("The element with the attribute [fieldset__container] not found in your fieldset__parent");
            }
            const fieldSets = fieldsetContainer.querySelectorAll('fieldset');
            if(!fieldSets){
                throw new Error("Aucune section de formulaire trouvée.");
            }
            const next_buttons = form.querySelectorAll("[__next__]");
            const prev_buttons = form.querySelectorAll("[__prev__]");
            if(!next_buttons){
                throw new Error('No "next" button found!');
            }
            if(!prev_buttons){
                throw new Error("No 'previous' button found!");
            }
        } catch (error) {
            throw error;
        }
    }
    /**
     * Defines the movement of each fieldset.
     * @param params The parameters for setting translateX.
     * @param fieldSet The fieldset element.
    */
    protected setTranslateX(fieldSet:HTMLElement,progressOptions?: ProgressFormType){
        if(!fieldSet) return ;
        this.translateX = progressOptions && progressOptions.translateX ? progressOptions.translateX : - fieldSet.offsetWidth;
    }
}
